import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

type ResearchDirection = {
  _id?: string;
  order: number;
  title: string;
  summary: string;
  description: string;
  bullets: string[];
};

type ResearchPageData = {
  directions: ResearchDirection[];
};

const emptyDirection = (order: number): ResearchDirection => ({
  order,
  title: '',
  summary: '',
  description: '',
  bullets: [],
});

type InlineRichTextEditorProps = {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (html: string) => void;
};

const InlineRichTextEditor: React.FC<InlineRichTextEditorProps> = ({
  label,
  value,
  placeholder,
  onChange,
}) => {
  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ script: 'sub' }, { script: 'super' }],
      ['clean'],
    ],
  };
  const formats = ['bold', 'italic', 'underline', 'script'];

  return (
    <div>
      <label className="mb-1 block font-medium text-neutral-700">{label}</label>
      <div className="rounded-md border border-neutral-300 bg-white">
        <ReactQuill
          className="inline-richtext-editor"
          theme="snow"
          value={value}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
        />
      </div>
    </div>
  );
};

const stripHtml = (value: string = '') =>
  value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

const AdminResearch: React.FC = () => {
  const [directions, setDirections] = useState<ResearchDirection[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get<ResearchPageData>('/research-page');
      setDirections(
        (res.data.directions ?? []).map((d, i) => ({
          order: d.order ?? i,
          title: d.title ?? '',
          summary: d.summary ?? '',
          description: d.description ?? '',
          bullets: Array.isArray(d.bullets) ? d.bullets : [],
        }))
      );
    } catch (err) {
      console.error(err);
      setError('Load failed.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateDirection = (index: number, patch: Partial<ResearchDirection>) => {
    setDirections((prev) =>
      prev.map((d, i) => (i === index ? { ...d, ...patch } : d))
    );
  };

  const setDirectionBullets = (index: number, text: string) => {
    const bullets = text
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    updateDirection(index, { bullets });
  };

  const addDirection = () => {
    setDirections((prev) => [...prev, emptyDirection(prev.length)]);
    setExpandedIndex(directions.length);
  };

  const removeDirection = (index: number) => {
    if (!window.confirm('Are you sure you want to delete this research direction?')) return;
    setDirections((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.map((d, i) => ({ ...d, order: i }));
    });
    if (expandedIndex === index) setExpandedIndex(null);
    else if (expandedIndex != null && expandedIndex > index)
      setExpandedIndex(expandedIndex - 1);
  };

  const moveUp = (index: number) => {
    if (index <= 0) return;
    setDirections((prev) => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next.map((d, i) => ({ ...d, order: i }));
    });
    setExpandedIndex(index - 1);
  };

  const moveDown = (index: number) => {
    if (index >= directions.length - 1) return;
    setDirections((prev) => {
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next.map((d, i) => ({ ...d, order: i }));
    });
    setExpandedIndex(index + 1);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const payload = {
        directions: directions.map((d, i) => ({
          order: i,
          title: d.title,
          summary: d.summary,
          description: d.description,
          bullets: d.bullets,
        })),
      };
      await api.put('/research-page', payload);
      await load();
    } catch (err) {
      console.error(err);
      setError('Save failed. Please try again later.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-neutral-600">Loading...</p>;
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-serif text-2xl font-semibold text-neutral-900">
          Research Management
        </h1>
        <button
          onClick={handleSave}
          disabled={saving}
          className="rounded-full bg-brand-red px-4 py-1.5 text-xs font-semibold tracking-[0.18em] text-white hover:bg-red-700 disabled:opacity-70"
        >
          {saving ? 'Saving...' : 'Save all'}
        </button>
      </div>
      <p className="mb-4 text-xs text-neutral-500">
        Manage research directions for the Research page and homepage overview.
        Edit the page intro in{' '}
        <Link to="/admin/page-intros" className="font-semibold text-brand-red hover:underline">
          Page Intro Configuration
        </Link>
        .
      </p>

      <div className="space-y-6">
        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-neutral-700">
              Direction list (display order)
            </span>
            <button
              onClick={addDirection}
              className="rounded-full border border-brand-red px-3 py-1 text-xs font-semibold text-brand-red hover:bg-brand-red hover:text-white"
            >
              Add Direction
            </button>
          </div>
          <div className="space-y-3">
            {directions.map((d, index) => (
              <div
                key={index}
                className="rounded-lg border border-neutral-200 bg-white overflow-hidden"
              >
                <div
                  className="flex items-center justify-between px-4 py-2 bg-neutral-50 cursor-pointer"
                  onClick={() =>
                    setExpandedIndex(expandedIndex === index ? null : index)
                  }
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-neutral-500">
                      Direction {index + 1}
                    </span>
                    <span className="text-sm font-medium text-neutral-900">
                      {stripHtml(d.title) || '(Untitled)'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => moveUp(index)}
                      disabled={index === 0}
                      className="rounded px-1.5 py-0.5 text-[11px] text-neutral-600 hover:bg-neutral-200 disabled:opacity-40"
                    >
                      Move up
                    </button>
                    <button
                      type="button"
                      onClick={() => moveDown(index)}
                      disabled={index === directions.length - 1}
                      className="rounded px-1.5 py-0.5 text-[11px] text-neutral-600 hover:bg-neutral-200 disabled:opacity-40"
                    >
                      Move down
                    </button>
                    <button
                      type="button"
                      onClick={() => removeDirection(index)}
                      className="rounded px-1.5 py-0.5 text-[11px] text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                {expandedIndex === index && (
                  <div className="border-t border-neutral-200 p-4 space-y-3 text-xs">
                    <InlineRichTextEditor
                      label="Title"
                      value={d.title}
                      placeholder="e.g. Catalyst interface engineering"
                      onChange={(html) => updateDirection(index, { title: html })}
                    />
                    <InlineRichTextEditor
                      label="Short description (homepage overview)"
                      value={d.summary}
                      placeholder="A short sentence shown on homepage cards"
                      onChange={(html) => updateDirection(index, { summary: html })}
                    />
                    <InlineRichTextEditor
                      label="Detailed description (Research page body)"
                      value={d.description}
                      placeholder="Detailed paragraph for this direction"
                      onChange={(html) =>
                        updateDirection(index, {
                          description: html,
                        })
                      }
                    />
                    <div>
                      <label className="mb-1 block font-medium text-neutral-700">
                        Bullet items (one per line)
                      </label>
                      <textarea
                        value={d.bullets.join('\n')}
                        onChange={(e) =>
                          setDirectionBullets(index, e.target.value)
                        }
                        rows={4}
                        className="w-full rounded-md border border-neutral-300 px-2 py-1.5 outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
                        placeholder="One item per line, e.g.&#10;· Catalyst interface engineering"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
            {directions.length === 0 && (
              <p className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 px-4 py-6 text-center text-xs text-neutral-500">
                No research directions yet. Click Add Direction to create one.
              </p>
            )}
          </div>
        </div>
      </div>

      {error && (
        <p className="mt-4 text-xs text-red-600">{error}</p>
      )}
    </div>
  );
};

export default AdminResearch;
